import { ORCHESTRATION_WS_METHODS, WS_METHODS } from "@afar-research/contracts";
import * as Cause from "effect/Cause";
import * as Context from "effect/Context";
import type * as Duration from "effect/Duration";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";
import { RpcClientError } from "effect/unstable/rpc";

import { EnvironmentSupervisor } from "../connection/supervisor.ts";
import {
  clientTransportEnvironmentOwner,
  ClientTransportResources,
  trackClientTransportEffect,
} from "../connection/resources.ts";
import type { WsRpcProtocolClient } from "../rpc/protocol.ts";

export class EnvironmentRpcUnavailableError extends Schema.TaggedErrorClass<EnvironmentRpcUnavailableError>()(
  "EnvironmentRpcUnavailableError",
  {
    environmentId: Schema.String,
    message: Schema.String,
  },
) {}

export interface EnvironmentRpcRequestObservation {
  readonly environmentId: string;
  readonly method: string;
}

export class EnvironmentRpcRequestObserver extends Context.Reference<{
  readonly observe: (
    request: EnvironmentRpcRequestObservation,
  ) => Effect.Effect<Effect.Effect<void>>;
}>("@afar-research/client-runtime/rpc/EnvironmentRpcRequestObserver", {
  defaultValue: () => ({
    observe: () => Effect.succeed(Effect.void),
  }),
}) {}

export type EnvironmentRpcTag = keyof WsRpcProtocolClient & string;
type RpcMethod<TTag extends EnvironmentRpcTag> = WsRpcProtocolClient[TTag];

export type EnvironmentSubscriptionRpcTag =
  | typeof ORCHESTRATION_WS_METHODS.synchronizeHostView
  | typeof ORCHESTRATION_WS_METHODS.subscribeThread
  | typeof WS_METHODS.subscribeServerConfig
  | typeof WS_METHODS.subscribeServerLifecycle
  | typeof WS_METHODS.subscribeProjectActionReadiness
  | typeof WS_METHODS.projectContributionGenerationEvents
  | typeof WS_METHODS.projectsSubscribeFile
  | typeof WS_METHODS.subscribeProviderMcp
  | typeof WS_METHODS.subscribeTerminalEvents
  | typeof WS_METHODS.subscribeTerminalMetadata
  | typeof WS_METHODS.subscribePreviewEvents
  | typeof WS_METHODS.subscribeDiscoveredLocalServers
  | typeof WS_METHODS.computerUseServiceStatus
  | typeof WS_METHODS.previewAutomationConnect
  | typeof WS_METHODS.subscribeVcsStatus
  | typeof WS_METHODS.terminalAttach
  | typeof WS_METHODS.workflowSubscribe;

export type EnvironmentStreamCommandRpcTag = typeof WS_METHODS.gitRunStackedAction;

export type EnvironmentStreamRpcTag =
  | EnvironmentSubscriptionRpcTag
  | EnvironmentStreamCommandRpcTag;

export type EnvironmentUnaryRpcTag = Exclude<EnvironmentRpcTag, EnvironmentStreamRpcTag>;
const isRpcClientError = Schema.is(RpcClientError.RpcClientError);

export type EnvironmentRpcInput<TTag extends EnvironmentRpcTag> = Parameters<RpcMethod<TTag>>[0];

export type EnvironmentRpcSuccess<TTag extends EnvironmentUnaryRpcTag> =
  RpcMethod<TTag> extends (input: any, options?: any) => Effect.Effect<infer A, any, any>
    ? A
    : never;

export type EnvironmentRpcFailure<TTag extends EnvironmentUnaryRpcTag> =
  RpcMethod<TTag> extends (input: any, options?: any) => Effect.Effect<any, infer E, any>
    ? E
    : never;

export type EnvironmentRpcStreamValue<TTag extends EnvironmentStreamRpcTag> =
  RpcMethod<TTag> extends (input: any, options?: any) => Stream.Stream<infer A, any, any>
    ? A
    : never;

export type EnvironmentRpcStreamFailure<TTag extends EnvironmentStreamRpcTag> =
  RpcMethod<TTag> extends (input: any, options?: any) => Stream.Stream<any, infer E, any>
    ? E
    : never;

const currentSession = Effect.fn("EnvironmentRpc.currentSession")(function* () {
  const supervisor = yield* EnvironmentSupervisor;
  return yield* SubscriptionRef.get(supervisor.session).pipe(
    Effect.flatMap(
      Option.match({
        onNone: () =>
          Effect.fail(
            new EnvironmentRpcUnavailableError({
              environmentId: supervisor.target.environmentId,
              message: `${supervisor.target.label} is not connected.`,
            }),
          ),
        onSome: Effect.succeed,
      }),
    ),
  );
});

export const request = Effect.fn("EnvironmentRpc.request")(function* <
  TTag extends EnvironmentUnaryRpcTag,
>(tag: TTag, input: EnvironmentRpcInput<TTag>) {
  const supervisor = yield* EnvironmentSupervisor;
  yield* Effect.annotateCurrentSpan({
    "environment.id": supervisor.target.environmentId,
    "rpc.method": tag,
  });
  const session = yield* currentSession();
  const observer = yield* EnvironmentRpcRequestObserver;
  const method = session.client[tag] as (
    input: EnvironmentRpcInput<TTag>,
  ) => Effect.Effect<EnvironmentRpcSuccess<TTag>, EnvironmentRpcFailure<TTag>>;
  const completeObservation = yield* observer.observe({
    environmentId: supervisor.target.environmentId,
    method: tag,
  });
  return yield* method(input).pipe(Effect.ensuring(completeObservation));
});

export function runStream<TTag extends EnvironmentStreamCommandRpcTag>(
  tag: TTag,
  input: EnvironmentRpcInput<TTag>,
): Stream.Stream<
  EnvironmentRpcStreamValue<TTag>,
  EnvironmentRpcStreamFailure<TTag> | EnvironmentRpcUnavailableError,
  EnvironmentSupervisor
> {
  return Stream.unwrap(
    currentSession().pipe(
      Effect.map((session) => {
        const method = session.client[tag] as (
          input: EnvironmentRpcInput<TTag>,
        ) => Stream.Stream<EnvironmentRpcStreamValue<TTag>, EnvironmentRpcStreamFailure<TTag>>;
        return method(input);
      }),
    ),
  ).pipe(
    Stream.withSpan("EnvironmentRpc.runStream", {
      attributes: { "rpc.method": tag },
    }),
  );
}

export function subscribe<TTag extends EnvironmentSubscriptionRpcTag>(
  tag: TTag,
  input: EnvironmentRpcInput<TTag>,
  options?: {
    readonly onExpectedFailure?: (
      cause: Cause.Cause<EnvironmentRpcStreamFailure<TTag>>,
    ) => Effect.Effect<void, never, never>;
    readonly retryExpectedFailureAfter?:
      | Duration.Input
      | ((
          consecutiveFailureCount: number,
          cause: Cause.Cause<EnvironmentRpcStreamFailure<TTag>>,
        ) => Duration.Input | null);
  },
): Stream.Stream<
  EnvironmentRpcStreamValue<TTag>,
  EnvironmentRpcStreamFailure<TTag>,
  EnvironmentSupervisor
> {
  return Stream.unwrap(
    Effect.all([EnvironmentSupervisor, ClientTransportResources]).pipe(
      Effect.map(([supervisor, resources]) => {
        const ownerIdentity = clientTransportEnvironmentOwner(supervisor.target.environmentId);
        const subscription = SubscriptionRef.changes(supervisor.session).pipe(
          Stream.switchMap(
            Option.match({
              onNone: () => Stream.empty,
              onSome: (session) => {
                const method = session.client[tag] as (
                  input: EnvironmentRpcInput<TTag>,
                ) => Stream.Stream<
                  EnvironmentRpcStreamValue<TTag>,
                  EnvironmentRpcStreamFailure<TTag>
                >;
                let consecutiveExpectedFailureCount = 0;
                const subscribeToSession = (): Stream.Stream<
                  EnvironmentRpcStreamValue<TTag>,
                  EnvironmentRpcStreamFailure<TTag>
                > =>
                  Stream.suspend(() =>
                    method(input).pipe(
                      Stream.tap(() =>
                        Effect.sync(() => {
                          consecutiveExpectedFailureCount = 0;
                        }),
                      ),
                      Stream.catchCause((cause) => {
                        const hasOnlyExpectedFailures =
                          cause.reasons.length > 0 &&
                          cause.reasons.every((reason) => reason._tag === "Fail");
                        const isTransportFailure =
                          hasOnlyExpectedFailures &&
                          cause.reasons.every(
                            (reason) => reason._tag === "Fail" && isRpcClientError(reason.error),
                          );
                        if (isTransportFailure) {
                          return Stream.fromEffect(
                            Effect.logWarning(
                              "Durable RPC subscription lost its transport; waiting for the next session.",
                              {
                                cause: Cause.pretty(cause),
                                method: tag,
                                environmentId: supervisor.target.environmentId,
                              },
                            ),
                          ).pipe(Stream.drain);
                        }
                        if (hasOnlyExpectedFailures && options?.onExpectedFailure !== undefined) {
                          const handled = Stream.fromEffect(options.onExpectedFailure(cause)).pipe(
                            Stream.drain,
                          );
                          if (options.retryExpectedFailureAfter === undefined) {
                            return handled;
                          }
                          const retryDelay =
                            typeof options.retryExpectedFailureAfter === "function"
                              ? options.retryExpectedFailureAfter(
                                  consecutiveExpectedFailureCount,
                                  cause,
                                )
                              : options.retryExpectedFailureAfter;
                          consecutiveExpectedFailureCount += 1;
                          if (retryDelay === null) {
                            return handled;
                          }
                          return handled.pipe(
                            Stream.concat(
                              Stream.fromEffect(
                                trackClientTransportEffect(
                                  resources,
                                  {
                                    ownerIdentity,
                                    kind: "timer",
                                    identity: `rpc-subscription-retry:${tag}`,
                                    lifetime: "connection",
                                  },
                                  Effect.sleep(retryDelay),
                                  { interruptionReason: "reconnect-settled" },
                                ),
                              ).pipe(Stream.drain),
                            ),
                            Stream.concat(subscribeToSession()),
                          );
                        }
                        return Stream.failCause(cause);
                      }),
                    ),
                  );
                return subscribeToSession();
              },
            }),
          ),
        );
        return Stream.suspend(() => {
          const lease = resources.register({
            ownerIdentity,
            kind: "subscription",
            identity: `rpc:${tag}`,
            lifetime: "retained-owner",
          });
          return subscription.pipe(
            Stream.ensuring(
              Effect.sync(() => {
                lease.release("interruption");
              }),
            ),
          );
        });
      }),
    ),
  ).pipe(
    Stream.withSpan("EnvironmentRpc.subscribe", {
      attributes: { "rpc.method": tag },
    }),
  );
}

export const config = Effect.gen(function* () {
  const session = yield* currentSession();
  return yield* session.initialConfig;
}).pipe(Effect.withSpan("EnvironmentRpc.config"));
